import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Avatar,
  Button,
  Icon,
  useToast,
  Input,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { FiPlus, FiX } from "react-icons/fi";
import {
  addCardMember,
  removeCardMember,
  fetchAdminsForMembers,
} from'../../../../../ProjectBoardServices/BoardModalAPI/RightModalAPI/InfoTabAPI/MembersAPI';

const Members = ({ task }) => {
  const [members, setMembers] = useState(task?.members || []);
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  // Helper function to get full name
  const getFullName = (user) => {
    const firstName = user.admin_firstname || user.firstName || "";
    const lastName = user.admin_lastname || user.lastName || "";
    return `${firstName} ${lastName}`.trim();
  };

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        // ✅ Service function use kar rahe hain
        const res = await fetchAdminsForMembers(0);

        // console.log("Users API Response ===>", res.data);

        if (res.status === 200) {
          const users = res.data?.data || res.data?.admins || res.data?.users || res.data || [];
          setAllUsers(users);
        }
      } catch (error) {
        console.error("Failed to fetch users", error);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  // Add Member
  const addMember = async (cardId, memberId) => {
    try {
      setLoadingAdd(true);
      // ✅ Service function use kar rahe hain
      const res = await addCardMember(cardId, memberId);

      console.log("Add Member Response ===>", res.data);

      if (res.status === 200) {
        // Find the added user from allUsers list and add to members
        const addedUser = allUsers.find((u) => u.admin_id === memberId);
        if (addedUser && !members.find((m) => (m.id || m.admin_id) === memberId)) {
          setMembers((prev) => [
            ...prev,
            {
              id: addedUser.admin_id,
              admin_id: addedUser.admin_id,
              name: getFullName(addedUser),
              admin_firstname: addedUser.admin_firstname,
              admin_lastname: addedUser.admin_lastname,
              firstName: addedUser.admin_firstname,
              lastName: addedUser.admin_lastname,
              email: addedUser.admin_email,
              admin_email: addedUser.admin_email,
              admin_img: addedUser.admin_img,
            },
          ]);
        }

        toast({
          title: "Member added",
          status: "success",
          duration: 2000,
        });
      }
    } catch (error) {
      toast({
        title: "Failed to add member",
        status: "error",
        duration: 2000,
      });
      console.error(error);
    } finally {
      setLoadingAdd(false);
    }
  };

  // Remove Member
  const removeMember = async (cardId, memberId) => {
    try {
      // ✅ Service function use kar rahe hain
      const res = await removeCardMember(cardId, memberId);

      console.log("Remove Member Response ===>", res.data);

      if (res.status === 200) {
        setMembers((prev) => prev.filter((m) => (m.id || m.admin_id) !== memberId));

        toast({
          title: "Member removed",
          status: "info",
          duration: 2000,
        });
      }
    } catch (error) {
      toast({
        title: "Failed to remove member",
        status: "error",
        duration: 2000,
      });
      console.error(error);
    }
  };

  // Handle remove button click - open confirmation dialog
  const handleRemoveClick = (member) => {
    setMemberToRemove(member);
    onOpen();
  };

  // Confirm removal
  const confirmRemoval = () => {
    if (memberToRemove) {
      removeMember(task.id, memberToRemove.id || memberToRemove.admin_id);
    }
    onClose();
    setMemberToRemove(null);
  };

  // Cancel removal
  const cancelRemoval = () => {
    onClose();
    setMemberToRemove(null);
  };

  // Filter users excluding already added members
  const filteredUsers = (allUsers || []).filter((user) => {
    const isAlreadyMember = members.some((m) => (m.id || m.admin_id) === user.admin_id);
    const fullName = getFullName(user);
    const matchesSearch =
      fullName.toLowerCase().includes(search.toLowerCase()) ||
      (user.admin_email || "").toLowerCase().includes(search.toLowerCase());
    return !isAlreadyMember && matchesSearch;
  });

  return (
    <Box mt={4}>
      <Text
        fontSize="12px"
        fontWeight="600"
        color="#6B778C"
        mb={2}
        textTransform="uppercase"
      >
        MEMBERS
      </Text>

      <VStack align="stretch" spacing={2}>
        {members && members.length > 0 ? (
          members.map((member, index) => {
            const memberName = member.name || getFullName(member);
            return (
              <HStack
                key={member.id || member.admin_id || index}
                spacing={2}
                justify="space-between"
              >
                <HStack spacing={2}>
                  <Avatar
                    size="sm"
                    name={memberName}
                    w="32px"
                    h="32px"
                    src={member.admin_img}
                  />
                  <VStack align="start" spacing={0}>
                    <Text fontSize="14px" color="#172B4D" fontWeight="500">
                      {memberName}
                    </Text>
                    <Text fontSize="12px" color="#6B778C">
                      {member.email || member.admin_email}
                    </Text>
                  </VStack>
                </HStack>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => handleRemoveClick(member)}
                  color="red.500"
                  aria-label="Remove"
                  title="Remove"
                >
                  <FiX />
                </Button>
              </HStack>
            );
          })
        ) : (
          <Text fontSize="14px" color="#6B778C">
            No members available.
          </Text>
        )}

        {/* Add Members Dropdown with scrollable list */}
        <Menu closeOnSelect={true}>
          <MenuButton
            as={Button}
            size="sm"
            variant="ghost"
            leftIcon={<Icon as={FiPlus} />}
            color="#0065FF"
            fontSize="14px"
            h="32px"
            justifyContent="flex-start"
            px={0}
            isLoading={loadingAdd}
            _hover={{ bg: "transparent", textDecoration: "underline" }}
          >
            Add Members
          </MenuButton>
          <MenuList p={2} minW="280px" maxH="300px" bg="white">
            <Input
              placeholder="Search by name or email..."
              size="sm"
              mb={2}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              bg="white"
              color="black"
            />
            <Box maxH="200px" overflowY="auto">
              {loadingUsers ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <Spinner size="sm" />
                </Box>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.slice(0, 50).map((user) => {
                  const fullName = getFullName(user);
                  return (
                    <MenuItem
                      key={user.admin_id}
                      onClick={() => addMember(task.id, user.admin_id)}
                      py={2}
                      bg="white"
                      _hover={{ bg: "gray.100" }}
                    >
                      <HStack spacing={3}>
                        <Avatar size="sm" name={fullName} src={user.admin_img} />
                        <VStack align="start" spacing={0}>
                          <Text fontSize="14px" fontWeight="500" color="black">
                            {fullName}
                          </Text>
                          <Text fontSize="12px" color="gray.600">
                            {user.admin_email}
                          </Text>
                        </VStack>
                      </HStack>
                    </MenuItem>
                  );
                })
              ) : (
                <Box px={2} py={4} textAlign="center">
                  <Text fontSize="14px" color="gray.500">
                    {search ? "No users found" : "All users are already members"}
                  </Text>
                </Box>
              )}
            </Box>
          </MenuList>
        </Menu>
      </VStack>

      {/* Remove Confirmation Alert Dialog */}
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={cancelRemoval}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove Member
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to remove{" "}
              <Text as="span" fontWeight="bold">
                {memberToRemove
                  ? memberToRemove.name || getFullName(memberToRemove)
                  : "this member"}
              </Text>{" "}
              from this card? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={cancelRemoval}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmRemoval} ml={3}>
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Members;
